/**
 * Thin fetch wrapper for the smibo API.
 *
 * One thing it takes care of that every call needs:
 *  - `credentials: 'include'`, because auth is an HttpOnly session cookie rather than a bearer
 *    token in localStorage (nothing for XSS to steal).
 *
 * It no longer names a workspace. smibo serves one shop, fixed in the API's configuration, so
 * every request lands there without the SPA having to say so.
 */

import { t } from '@/i18n'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail: string | null,
    readonly errors: Record<string, string[]> = {},
    /** Values some messages need, keyed by the message key that needs them. */
    readonly messageArgs: Record<string, Record<string, unknown>> = {},
  ) {
    super(detail || title)
    this.name = 'ApiError'
  }

  /** Flattened field errors, ready for a form summary. Message keys, not sentences. */
  get fieldMessages(): string[] {
    return Object.values(this.errors).flat()
  }

  /**
   * One message key, resolved with whatever values it needs.
   *
   * Anything that is not a key falls through `t()` unchanged, which is what lets a database
   * message or a proxy's own wording pass by untouched.
   */
  translate(message: string): string {
    return t(message, this.messageArgs[message] ?? {})
  }

  /**
   * The message worth showing, resolved: the first field error, else the detail, else the title.
   *
   * A getter rather than that chain written out at every call site — it was, twenty times, and the
   * copy that forgot the arguments would have been indistinguishable from the ones that did not.
   */
  get text(): string {
    return this.translate(this.fieldMessages[0] ?? this.detail ?? this.title)
  }

  /** Every part of the failure, resolved and joined — for a form that shows them all at once. */
  get fullText(): string {
    return [this.detail ?? this.title, ...this.fieldMessages]
      .filter(Boolean)
      .map((message) => this.translate(message))
      .join(' ')
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** 402 is used for plan/seat limits, which deserve an upgrade prompt rather than an error toast. */
  get isPlanLimit(): boolean {
    return this.status === 402
  }
}

type QueryValue = string | number | boolean | null | undefined

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options

  const url = new URL(path, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (response.status === 204) return undefined as T

  const payload = await readJson(response)

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.title ?? response.statusText ?? 'Request failed',
      payload?.detail ?? null,
      payload?.errors ?? {},
      payload?.messageArgs ?? {},
    )
  }

  return payload as T
}

async function readJson(response: Response): Promise<any> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return { title: 'Unexpected response', detail: text.slice(0, 500) }
  }
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),
}
